import {
    formatActivityContactCompanyCollectionOptions,
    formatActivityContactOption,
    formatActivityProjectOptions,
    formatActivityPropertyOptions,
    formatActivityReminderChannelOptions,
    formatActivityReminderMinutesOptions,
} from '../../../../api/activities/formatters'
import activities from '../../../../api/activities/hooks'
import { formatAgentOptions } from '../../../../api/agents/formatters'
import agents from '../../../../api/agents/hooks'
import { formatContactFieldOptions } from '../../../../api/contacts/formatters'
import { SelectOption } from '../../../../api/globalTypes'
import { formatPropertyOptionById } from '../../../../api/properties/formatters'
import { EntityParams } from '../../../AppPermissionsSwitcher/EntityParamsContext'
import { DETAIL_VIEW } from '../../../../common/constants'
import { activityFields } from '../../../../common/forms/activities/fields'
import { ActivityModalData } from '../types'

/**
 * Provides select dropdown options for activity form fields
 *
 * @param event - Activity modal event data (edit mode), null/undefined in create mode
 * @param parentId - Optional parent entity ID (property/contact/company) for prefilling in create mode
 * @param detailView - Optional detail view context (DETAIL_VIEW.PROPERTY | DETAIL_VIEW.CONTACT | DETAIL_VIEW.COMPANY)
 * @param params - Params the opener's property/contact/company view query was made with, needed to match the React Query cache key
 * @returns Object containing select options for all activity form fields
 *
 */
export const useActivityFormSelectOptions = (
    event?: ActivityModalData | null,
    parentId?: string,
    detailView?: string,
    params?: EntityParams
) => {
    const { data: activityValues, isPending: isActivityValuesPending } = activities.FormValues.useQuery()
    const { data: agentsData, isPending: isAgentsPending } = agents.GetAgents.useQuery()

    let contactIdOptions: SelectOption[] = []
    let contactCompanyOptions: (SelectOption | { value: number; label?: string })[] = []

    // Edit mode: pull options from the activity
    if (event?.id) {
        contactIdOptions = formatActivityContactOption(event.contact)
        contactCompanyOptions = formatActivityContactCompanyCollectionOptions(event.contactCompany)
    }

    // Create mode from contact detail view: prefill contact and their companies
    if (!event?.id && detailView === DETAIL_VIEW.CONTACT && parentId) {
        contactIdOptions = formatContactFieldOptions(parentId, 'contactPerson', params)
        contactCompanyOptions = formatContactFieldOptions(parentId, 'contactPersonCompany', params)
    }

    // Create mode from company detail view: prefill company
    if (!event?.id && detailView === DETAIL_VIEW.COMPANY && parentId) {
        contactCompanyOptions = formatContactFieldOptions(parentId, 'contactCompany', params)
    }

    const selectOptions = {
        [activityFields.ACTIVITY_TYPE_ID]: activityValues?.type ?? [],
        [activityFields.AGENT_ID]: formatAgentOptions(agentsData),
        [activityFields.REMINDER_CHANNEL]: formatActivityReminderChannelOptions(),
        [activityFields.REMINDER_MINUTES_BEFORE]: formatActivityReminderMinutesOptions(),
        [activityFields.CONTACT_ID]: contactIdOptions,
        [activityFields.CONTACT_COMPANY_COLLECTION]: contactCompanyOptions,
        [activityFields.PROJECT_ID]: formatActivityProjectOptions(event?.project),
        [activityFields.PROPERTY_ID]:
            detailView !== DETAIL_VIEW.PROPERTY ? formatActivityPropertyOptions(event?.property) : formatPropertyOptionById(parentId, params),
    }

    return { selectOptions, isPending: isActivityValuesPending || isAgentsPending }
}
